import { EmptyQueryPropsFactory, IQueryProps } from './Query'

export interface IResponseProps<P> {
  items: P[]
  byIds?: {
    [id: string]: P
  }
  query: IQueryProps
  loading?: boolean
  last?: P | null
  top?: P | null
}

export default class ResponseEntity<P> {
  private _props: IResponseProps<P>

  constructor(props: IResponseProps<P>) {
    this._props = props
  }

  get props(): IResponseProps<P> {
    return this._props
  }

  get clone(): IResponseProps<P> {
    return EmptyResponsePropsFactory(structuredClone(this._props))
  }
}

export const EmptyResponsePropsFactory = <P>(props?: Partial<IResponseProps<P>> | null): IResponseProps<P> => ({
  items: [],
  query: EmptyQueryPropsFactory(),
  loading: false,
  last: null,
  ...props
})

export const ResponsePropsFactory = <P>(value: IResponseProps<P>, props: IResponseProps<P> | null): IResponseProps<P> => {
  const query = EmptyQueryPropsFactory({ ...props?.query })
  return {
    items: value.items.concat(props?.items || []),
    query: {
      ...query,
      page: query.page + 1
    },
    last: props ? props.items[0] : null
  }
}

export const ResponsePropsByIdsFactory = <P>(value: IResponseProps<P>, props: IResponseProps<P> | null, current?: any | null): IResponseProps<P> => {
  const query = EmptyQueryPropsFactory({ ...props?.query })
  const last = value.items[value.items.length - 1] || props?.items[0] || null

  let byIds = {}
  if (current) {
    byIds = { [current.id]: current }
  }
  byIds = { ...byIds, ...value.byIds } // 既存のbyIdsをマージ

  // 新しいデータがあれば、byIdsに追加する
  if (props?.items) {
    for (const prop of props.items as any) {
      byIds = { ...byIds, [prop.id]: prop }
    }
  }
  const items: P[] = Object.values(byIds) // 新しいitemsを作成

  return {
    byIds,
    items,
    query: {
      ...query,
      page: query.page + 1
    },
    last
  }
}

export const SetResponsePropsByIds = <P>(value: IResponseProps<P>, current: any, force: boolean = false): IResponseProps<P> => {
  if (!current?.id) return value
  const byIds = force ? { ...value.byIds, [current.id]: current } : { [current.id]: current, ...value.byIds, [current.id]: current }
  const items: P[] = Object.values(byIds)
  return {
    ...value,
    byIds,
    items,
    query: {
      ...value.query,
      count: (value.query.count || 0) + 1
    },
    top: force ? null : current
  }
}

export const UpdateResponsePropsByIds = <P>(value: IResponseProps<P>, current: any): IResponseProps<P> => {
  if (!current?.id) return value
  const byIds = structuredClone(value.byIds)
  if (!byIds?.[current.id]) return value
  byIds[current.id] = current
  const items: P[] = Object.values(byIds)
  return {
    ...value,
    byIds,
    items
  }
}

export const DeleteResponsePropsByIds = <P>(value: IResponseProps<P>, id: string): IResponseProps<P> => {
  const byIds = structuredClone(value.byIds)
  let items: P[] = []
  if (byIds && byIds[id]) {
    delete byIds[id]
    items = Object.values(byIds)
  }
  return {
    ...value,
    byIds,
    items
  }
}
